import React, { useState, useRef, useEffect } from "react";
import { AlertCircle, CheckCircle } from "lucide-react";

const reasons = {
  E1: "Accident",
  E2: "Vehicle Breakdown",
  E3: "Medical Emergency",
  E4: "Security Threat",
  E5: "Other",
};

function DraggableAlertWindow({ alert, isResolving, onResolve, index }) {
  const [position, setPosition] = useState(() => ({
    x: 24 + index * 32,
    y: 80 + index * 24,
  }));
  const [isDragging, setIsDragging] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const containerRef = useRef(null);

  const startDrag = (event) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    dragOffset.current = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (event) => {
      const newX = event.clientX - dragOffset.current.x;
      const newY = event.clientY - dragOffset.current.y;

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const el = containerRef.current;
      const panelWidth = el ? el.offsetWidth : 0;
      const panelHeight = el ? el.offsetHeight : 0;

      const minX = 8;
      const minY = 8;
      const maxX = Math.max(minX, viewportWidth - panelWidth - 8);
      const maxY = Math.max(minY, viewportHeight - panelHeight - 8);

      const clampedX = Math.min(Math.max(minX, newX), maxX);
      const clampedY = Math.min(Math.max(minY, newY), maxY);

      setPosition({ x: clampedX, y: clampedY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  const {
    driverName,
    vehicleId,
    ecode,
    ediscription,
    driverSno,
    mobileNo,
  } = alert;

  const reasonLabel = reasons[ecode] || ecode || "Unknown Emergency";

  return (
    <div
      ref={containerRef}
      className="fixed z-[9999] pointer-events-none"
      style={{ top: position.y, left: position.x }}
    >
      <div className="flex flex-col max-h-[80vh] w-[80vw] sm:w-[420px] pointer-events-auto shadow-2xl">
        {/* Drag / Minimize Header */}
        <div
          className="flex items-center justify-between w-full bg-red-700 text-white px-3 py-1 cursor-move select-none rounded-t-xl"
          onMouseDown={startDrag}
        >
          <span className="font-semibold text-sm">
            SOS Alert - {vehicleId || "Unknown"}
          </span>
          <button
            type="button"
            onClick={() => setIsMinimized((prev) => !prev)}
            className="ml-2 text-xs border border-red-100 rounded px-2 py-0.5 hover:bg-red-600"
          >
            {isMinimized ? "Maximize" : "Minimize"}
          </button>
        </div>

        {!isMinimized && (
          <div className="relative w-full rounded-b-xl bg-white overflow-hidden flex flex-col border border-red-200">
            {/* Header - Blinking Red */}
            <div className="flex items-center justify-between bg-red-600 px-4 py-3 text-white animate-pulse-slow">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-8 w-8" />
                <div>
                  <h2 className="text-xl font-black uppercase tracking-widest">
                    Emergency
                  </h2>
                  <p className="text-xs font-medium text-red-100">
                    Immediate Action Required
                  </p>
                </div>
              </div>
              <div className="text-right">
                <span className="block text-2xl font-bold">{ecode}</span>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4 flex-1">
              <div className="grid grid-cols-2 gap-4 p-3 bg-red-50 rounded-xl border border-red-100">
                <div>
                  <span className="text-[10px] uppercase font-bold text-gray-500 tracking-wide">
                    Driver
                  </span>
                  <p
                    className="text-base font-bold text-gray-800 truncate"
                    title={driverName}
                  >
                    {driverName || "Unknown"}
                  </p>
                  {driverSno && (
                    <p className="text-xs font-semibold text-gray-600">
                      ID: {driverSno}
                    </p>
                  )}
                </div>
                <div>
                  <span className="text-[10px] uppercase font-bold text-gray-500 tracking-wide">
                    Vehicle ID
                  </span>
                  <p className="text-base font-bold text-gray-800">
                    {vehicleId || "Unknown"}
                  </p>
                </div>

                <div>
                  <span className="text-[10px] uppercase font-bold text-gray-500 tracking-wide">
                    Contact
                  </span>
                  <p className="text-base font-bold text-red-900 tracking-wide">
                    {mobileNo || "N/A"}
                  </p>
                </div>

                <div>
                  <span className="text-[10px] uppercase font-bold text-gray-500 tracking-wide">
                    Reason
                  </span>
                  <p className="text-lg font-bold text-red-700 leading-tight">
                    {reasonLabel}
                  </p>
                </div>
              </div>

              <div>
                <span className="text-[10px] uppercase font-bold text-gray-500 tracking-wide">
                  Message
                </span>
                <div className="mt-1 text-sm text-gray-800 bg-white p-3 border border-gray-200 rounded-lg shadow-inner min-h-[60px] max-h-[100px] overflow-auto">
                  {ediscription || "No details provided."}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end pt-3 border-t border-gray-100">
                <button
                  onClick={() => onResolve(alert)}
                  disabled={isResolving}
                  className={`flex items-center justify-center gap-2 rounded-lg px-6 py-2.5 text-base font-bold text-white shadow-lg transition-all w-full ${
                    isResolving
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-green-600 hover:bg-green-700 hover:scale-[1.02]"
                  }`}
                >
                  {isResolving ? (
                    <span>Processing...</span>
                  ) : (
                    <>
                      <CheckCircle className="h-5 w-5" /> Resolve & Clear
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function EmergencyNotificationModal({ alerts, onResolve, resolvingIds }) {
  const [showOverlay, setShowOverlay] = useState(false);
  const overlayTimeoutRef = useRef(null);

  // Full-screen red alert overlay for first 5 seconds when alerts arrive
  useEffect(() => {
    if (!alerts || alerts.length === 0) {
      setShowOverlay(false);
      if (overlayTimeoutRef.current) {
        clearTimeout(overlayTimeoutRef.current);
        overlayTimeoutRef.current = null;
      }
      return;
    }

    setShowOverlay(true);
    if (overlayTimeoutRef.current) {
      clearTimeout(overlayTimeoutRef.current);
    }
    overlayTimeoutRef.current = setTimeout(() => {
      setShowOverlay(false);
      overlayTimeoutRef.current = null;
    }, 5000);
  }, [alerts]);

  useEffect(() => {
    return () => {
      if (overlayTimeoutRef.current) {
        clearTimeout(overlayTimeoutRef.current);
      }
    };
  }, []);

  if (!alerts || alerts.length === 0) return null;

  return (
    <>
      {showOverlay && (
        <div className="fixed inset-0 z-[9998] bg-red-900 bg-opacity-80 animate-pulse-slow pointer-events-none" />
      )}

      {alerts.map((alert, index) => (
        <DraggableAlertWindow
          key={alert.vehicleId || index}
          alert={alert}
          index={index}
          isResolving={Array.isArray(resolvingIds) && alert.vehicleId ? resolvingIds.includes(alert.vehicleId) : false}
          onResolve={onResolve}
        />
      ))}
    </>
  );
}
